import { useAuthentication } from '@/composables/useAuthentication';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';
import { JsonLayerChartList, isJsonLayerChartList } from '@/interfaces/json-layer-responses/jsonLayerChartList';

interface AddChartListResponse extends JsonLayerResponse {
  chartList: JsonLayerChartList;
}

function isAddChartListResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is AddChartListResponse {
  return (
    'chartList' in jsonLayerResponse &&
    isJsonLayerChartList(jsonLayerResponse.chartList)
  );
}

export async function createChartList(chartListName: string): Promise<JsonLayerChartList> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to create ChartList - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'add-chartlist',
        chartCode: chartCodeCopy,
        name: chartListName
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`Create ChartList jsonLayerResponse for chartCode: ${chartCodeCopy} and name: ${chartListName}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to create ChartList for chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to create ChartList for chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to create ChartList for chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to create ChartList for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isAddChartListResponse(jsonLayerResponse)) {
    throw new Error(`Failed to create ChartList for chartCode: ${chartCodeCopy} - jsonLayerResponse.success is true but jsonLayerResponse does not match AddChartListResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  return jsonLayerResponse.chartList;
}

export async function addBulkSymbolsToChartList(chartListListNum: string, symbols: string, styleUrlSearchParams: URLSearchParams): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to create ChartList - user is not logged in.');
  }

  const chartCodeCopy = chartCode.value;

  let style = Array.from(styleUrlSearchParams.entries()) // TODO: json-layer wants the 'id' key to occur first, so this is a temporary hack to fix it. This is only an issue with "add-favorites-bulk" command
    .sort((a) => a[0] === 'id' ? -1 : 1)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  if (!style) {
    throw new Error('Unable to create ChartList - styleUrlSearchParams is empty.');
  }

  style = '&' + style;

  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'add-favorites-bulk',
        chartCode: chartCodeCopy,
        listNum: chartListListNum,
        symbols,
        style
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`Add bulk symbols to ChartList jsonLayerResponse for chartCode: ${chartCodeCopy} and listNum: ${chartListListNum} and style: ${style} and symbols: ${symbols}.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to add bulk symbols to ChartList for chartCode: ${chartCodeCopy} and listNum: ${chartListListNum} and style: ${style} and symbols: ${symbols}.`, e);

    throw new Error(`Failed to add bulk symbols to ChartList for chartCode: ${chartCodeCopy} and listNum: ${chartListListNum} and style: ${style} and symbols: ${symbols}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to add bulk symbols to ChartList for chartCode: ${chartCodeCopy} and listNum: ${chartListListNum} and style: ${style} and symbols: ${symbols} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to add bulk symbols to ChartList for chartCode: ${chartCodeCopy} and listNum: ${chartListListNum} and style: ${style} and symbols: ${symbols} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}
